import { AuthFetch, AuthFetchForm, AuthFetchRaw } from "../hooks/useApi";
import { RAGResponse } from "../types/rag.types";

export type StreamOutputChecklistPayload = {
  procedures: Array<{ code: string; description: string; quantity: string }>;
  patient_name: string;
  laudo: string;
  historico: string;
};

export type DescricaoResumidaPayload = {
  descricao_completa: string;
  patient_name: string;
};

export type DescricaoResumidaResponse = {
  descricao_resumida: string;
};

export type ResumoOpmePayload = {
  procedures: string[];
  resumoOPME: string;
  opme_data: [string, string][];
};

export type CreateChatPayload = {
  user_email: string;
  pacient_name: string;
  laudoExames: string;
  historiaPaciente: string;
  // Chave da especialidade do médico; Pedidos salvos lista só os pedidos dela.
  especialidade: string;
};

// Campos que a IA extrai da gravação da consulta (todos opcionais).
export type ConsultationFields = {
  nome_paciente?: string;
  historico_paciente?: string;
  laudo_paciente?: string;
  hospital?: string;
  tipo_de_cirurgia?: string;
};

export const chatService = {
  // Cria o pedido no Novo pedido; o timestamp devolvido identifica o pedido daí em diante.
  create: (apiFetch: AuthFetch, payload: CreateChatPayload): Promise<{ timestamp: string }> =>
    apiFetch<{ timestamp: string }>("/chat/create", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // Sugestão de procedimentos (TUSS) a partir do histórico e dos laudos.
  proceduresFromRag: (
    apiFetch: AuthFetch,
    payload: { query: string; specialty: string },
    signal: AbortSignal
  ): Promise<RAGResponse> =>
    apiFetch<RAGResponse>("/chat/get_procedures_from_rag", {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    }),

  // Ditado de um campo.
  transcribe: (apiFetchForm: AuthFetchForm, audio: File): Promise<{ text?: string }> => {
    const form = new FormData();
    form.append("file", audio);
    return apiFetchForm<{ text?: string }>("/transcribe", form);
  },

  // Gravação da consulta inteira. Pode passar de 60s, então quem chama passa o próprio signal.
  transcribeLong: (
    apiFetchForm: AuthFetchForm,
    audio: File,
    email: string,
    signal: AbortSignal
  ): Promise<{ json?: ConsultationFields }> => {
    const form = new FormData();
    form.append("file", audio);
    if (email) form.append("email", email);
    return apiFetchForm<{ json?: ConsultationFields }>("/transcribe-long", form, { signal });
  },

  // Justificativa técnica dos OPMEs (vai para a guia de OPME e o pedido).
  resumoOpme: (apiFetch: AuthFetch, payload: ResumoOpmePayload): Promise<{ response: string }> =>
    apiFetch<{ response: string }>("/chat/gerar_resumo_opme", {
      method: "POST",
      body: JSON.stringify(payload),
    }),


  streamOutputChecklist: (
    apiFetchRaw: AuthFetchRaw,
    payload: StreamOutputChecklistPayload
  ): Promise<Response> =>
    apiFetchRaw("/chat/generate_surgical_order", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // Condensa a "Descrição do paciente" completa do pedido numa versão <= 900
  // caracteres para a guia de internação. Stateless (~3-5s, chamada a LLM);
  // o servidor garante o teto de 900.
  descricaoResumida: (
    apiFetch: AuthFetch,
    payload: DescricaoResumidaPayload
  ): Promise<DescricaoResumidaResponse> =>
    apiFetch<DescricaoResumidaResponse>("/chat/descricao-resumida", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};